import { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Typography,
  Box,
  Alert,
  IconButton,
  Tooltip,
  Snackbar,
} from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import CheckIcon from '@mui/icons-material/Check';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import type { ApiKeyWithRawKey } from '@/types';

interface CreateApiKeyDialogProps {
  open: boolean;
  onClose: () => void;
  onCreate: (name: string) => Promise<ApiKeyWithRawKey>;
  isCreating: boolean;
}

export default function CreateApiKeyDialog({
  open,
  onClose,
  onCreate,
  isCreating,
}: CreateApiKeyDialogProps) {
  const [name, setName] = useState('');
  const [createdKey, setCreatedKey] = useState<ApiKeyWithRawKey | null>(null);
  const [copied, setCopied] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleCreate = async () => {
    try {
      setError(null);
      const newKey = await onCreate(name);
      setCreatedKey(newKey);
    } catch (err) {
      setError(err instanceof Error ? err.message : '创建 API Key 失败');
    }
  };

  const handleCopyKey = async () => {
    if (createdKey?.raw_key) {
      await navigator.clipboard.writeText(createdKey.raw_key);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const handleCopyConfig = async () => {
    if (createdKey?.raw_key) {
      const config = JSON.stringify(
        {
          mcpServers: {
            moemoe: {
              type: 'http',
              url: 'https://gbmrzpnasfxsewvxclun.supabase.co/functions/v1/mcp/mcp',
              headers: {
                Authorization: `Bearer ${createdKey.raw_key}`,
              },
            },
          },
        },
        null,
        2
      );
      await navigator.clipboard.writeText(config);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const getTraeInstallUrl = () => {
    if (!createdKey?.raw_key) return '';
    const config = JSON.stringify({
      type: 'http',
      url: 'https://gbmrzpnasfxsewvxclun.supabase.co/functions/v1/mcp/mcp',
      headers: {
        Authorization: `Bearer ${createdKey.raw_key}`,
      },
    });
    const encoder = new TextEncoder();
    const data = encoder.encode(config);
    let binary = '';
    for (let i = 0; i < data.byteLength; i++) {
      binary += String.fromCharCode(data[i]);
    }
    const configBase64 = btoa(binary);
    const params = new URLSearchParams();
    params.append('type', 'http');
    params.append('name', 'moemoe');
    params.append('config', configBase64);
    return `trae://trae.ai-ide/mcp-import?${params.toString()}`;
  };

  const handleClose = () => {
    setName('');
    setCreatedKey(null);
    setError(null);
    setCopied(false);
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        {createdKey ? 'API Key 创建成功！' : '创建新 API Key'}
      </DialogTitle>
      <DialogContent>
        {!createdKey ? (
          <>
            <TextField
              autoFocus
              margin="dense"
              label="Key 名称（可选）"
              fullWidth
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="例如：Claude Desktop, 我的项目"
              helperText="给 Key 起个名字，方便以后识别"
            />
            {error && (
              <Alert severity="error" sx={{ mt: 2 }}>
                {error}
              </Alert>
            )}
          </>
        ) : (
          <Box>
            <Alert severity="warning" sx={{ mb: 2 }}>
              <strong>请立即保存此 Key！</strong> 之后将无法再次查看。
            </Alert>

            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              您的 API Key:
            </Typography>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                p: 2,
                bgcolor: 'grey.900',
                borderRadius: 1,
                fontFamily: 'monospace',
                fontSize: '0.875rem',
                wordBreak: 'break-all',
              }}
            >
              <Typography
                component="code"
                sx={{
                  flex: 1,
                  color: 'success.light',
                  fontFamily: 'monospace',
                }}
              >
                {createdKey.raw_key}
              </Typography>
              <Tooltip title={copied ? '已复制！' : '复制到剪贴板'}>
                <IconButton onClick={handleCopyKey} size="small" color="inherit">
                  {copied ? <CheckIcon color="success" /> : <ContentCopyIcon />}
                </IconButton>
              </Tooltip>
            </Box>

            <Typography variant="body2" color="text.secondary" sx={{ mt: 3, mb: 1 }}>
              MCP (Claude/Cursor/Gemini/ChatGPT) 配置:
            </Typography>
            <Box
              sx={{
                p: 2,
                bgcolor: 'grey.900',
                borderRadius: 1,
                fontFamily: 'monospace',
                fontSize: '0.75rem',
                overflow: 'auto',
                maxHeight: 200,
              }}
            >
              <pre style={{ margin: 0, color: '#e0e0e0' }}>
{`{
  "mcpServers": {
    "moemoe": {
      "type": "http",
      "url": "https://gbmrzpnasfxsewvxclun.supabase.co/functions/v1/mcp/mcp",
      "headers": {
        "Authorization": "Bearer ${createdKey.raw_key}"
      }
    }
  }
}`}
              </pre>
            </Box>
            <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
              <Button
                variant="outlined"
                size="small"
                startIcon={<ContentCopyIcon />}
                onClick={handleCopyConfig}
              >
                复制配置
              </Button>
              <Button
                variant="contained"
                size="small"
                startIcon={<OpenInNewIcon />}
                href={getTraeInstallUrl()}
              >
                点击在 TRAE 中启用
              </Button>
            </Box>
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        {!createdKey ? (
          <>
            <Button onClick={handleClose}>取消</Button>
            <Button
              onClick={handleCreate}
              variant="contained"
              disabled={isCreating}
            >
              {isCreating ? '创建中...' : '创建 Key'}
            </Button>
          </>
        ) : (
          <Button onClick={handleClose} variant="contained">
            完成
          </Button>
        )}
      </DialogActions>

      <Snackbar
        open={copied}
        autoHideDuration={2000}
        onClose={() => setCopied(false)}
        message="已复制到剪贴板！"
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      />
    </Dialog>
  );
}
